import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import * as Unicons from '@iconscout/react-unicons';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import DataTables from '../../components/elements/DataTables';
import { useQuery } from '@tanstack/react-query';
import { getTransaksiRedeem } from '../../services/transaksiRedeem';
import Badge from '../../components/elements/Badge';
import ToolBoxSorting from '../../components/elements/ToolBox/ToolBoxSorting';
import LabelDate from '../../components/fragments/LabelDate';
import { Flex, Tooltip } from '@mantine/core';
import ModalActionVoucherRedeem from './ModalActionVoucherRedeem';

export type StatusRedeem = 'Approved' | 'Rejected' | 'Diambil';

const TransaksiRedeemPage = () => {
  const history = useHistory();
  const location = useLocation();

  const queryParams: any = new URLSearchParams(location.search);

  const paramsObject = {
    search: queryParams.get('search'),
    date: queryParams.get('date'),
    status: queryParams.get('status'),
    page: queryParams.get('page'),
    size: queryParams.get('size')
  };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    data: null,
    type: 'detail'
  });

  const [filter, setFilter] = useState({
    search: '',
    date: '',
    status: '',
    page: 1,
    size: 10
  });

  const columns = [
    {
      heading: 'Redeemer',
      props: 'redeemBy',
      id: 'redeemBy',
      sortable: true
    },
    {
      heading: "Voucher's Name",
      props: 'inventoryName',
      id: 'inventoryName',
      sortable: true
    },
    {
      heading: 'Redeem Date',
      props: (item) => <LabelDate date={item.dateIn} />,
      id: 'dateIn',
      sortable: true
    },
    {
      heading: 'Status',
      props: (item) => renderBadge(item.status),
      id: 'status',
      sortable: true
    },
    {
      heading: 'Updated By',
      props: 'userUp',
      id: 'userUp',
      sortable: true
    },
    {
      heading: 'Updated At',
      props: (item) => <LabelDate date={item.dateUp} />,
      id: 'dateUp',
      sortable: true
    },
    {
      heading: 'Action',
      props: (item) => renderAction(item),
      sortable: false
    }
  ];

  const renderAction = (item) => {
    if (item.status === 'Diambil') {
      return (
        <Tooltip label={'Action'}>
          <Flex
            justify={'center'}
            c={'blue'}
            style={{ cursor: 'pointer' }}
            align={'center'}
            onClick={() => handleOpenModal(item, 'action')}
            p={4}
          >
            <i className="fa fa-pencil"></i>
          </Flex>
        </Tooltip>
      );
    }

    return (
      <Tooltip label={'Detail'}>
        <Flex
          justify={'center'}
          c={'blue'}
          style={{ cursor: 'pointer' }}
          align={'center'}
          onClick={() => handleOpenModal(item, 'detail')}
          p={4}
        >
          <i className="fa fa-eye"></i>
        </Flex>
      </Tooltip>
    );
  };

  const renderBadge = (status: StatusRedeem) => {
    const color = () => {
      switch (status) {
        case 'Approved':
          return 'green';
        case 'Rejected':
          return 'red';
        default:
          return 'orange';
      }
    };

    const label = () => {
      switch (status) {
        case 'Approved':
          return 'Approved';
        case 'Rejected':
          return 'Rejected';
        default:
          return 'Waiting';
      }
    };
    return (
      <Badge variant="light" color={color()}>
        {label()}
      </Badge>
    );
  };

  // Queries
  const fetchedTransaksiRedeem = useQuery({
    queryKey: ['getTransaksiRedeem'],
    queryFn: () =>
      getTransaksiRedeem({
        search: filter.search,
        dateIn: filter.date,
        status: filter.status,
        page: filter.page,
        size: filter.size
      })
  });

  useEffect(() => {
    if (
      filter?.search ||
      paramsObject?.date ||
      paramsObject.status ||
      filter?.page ||
      filter?.size
    ) {
      fetchedTransaksiRedeem.refetch();
    }
  }, [filter]);

  useEffect(() => {
    if (
      paramsObject?.search ||
      paramsObject?.date ||
      paramsObject.status ||
      paramsObject?.page ||
      paramsObject?.size
    ) {
      setFilter({
        search: paramsObject?.search || '',
        date: paramsObject?.date || '',
        status: paramsObject?.status || '',
        page: paramsObject?.page || 1,
        size: paramsObject?.size || 10
      });
    }
  }, [
    paramsObject?.search,
    paramsObject?.date,
    paramsObject?.status,
    paramsObject?.page,
    paramsObject?.size
  ]);

  const buttonOnClick = () => {
    setModalConfig({
      show: true,
      data: null,
      type: null
    });
  };

  const handleSearch = useCallback((data: any) => {
    history.push({
      pathname: '/management/transaksi-redeem-reward',
      search: `?search=${data.search}&page=1&date=${data.date}&status=${data.status}`
    });
  }, []);

  const handleOpenModal = (data, type: 'detail' | 'action') => {
    setModalConfig({
      show: true,
      data: data,
      type: type
    });
  };

  const handleCloseModal = () => {
    setModalConfig({
      show: false,
      data: null,
      type: 'detail'
    });
  };

  return (
    <>
      <AdminLayout>
        <Breadcrumbs
          pageTitle="Redeemed Reward"
          itemList={[
            {
              title: 'Back Office BCL&D',
              href: '#',
              icon: <Unicons.UilFileCopyAlt size={16} />
            },
            {
              title: 'Reward & Gamification',
              href: '#'
            },
            {
              title: 'Redeemed Reward',
              href: '/management/transaksi-redeem-reward'
            }
          ]}
        />
        <Card>
          <ToolBoxSorting
            handleSearch={handleSearch}
            buttonOnClick={buttonOnClick}
            isSortingDate={true}
            buttonText="Create Level"
            withSelect
            withButton={false}
          />
          <DataTables
            columns={columns}
            loading={
              fetchedTransaksiRedeem?.isLoading ||
              fetchedTransaksiRedeem?.isFetching
            }
            data={fetchedTransaksiRedeem?.data?.data?.data || []}
            handleEdit={(data) => {
              setModalConfig({
                show: true,
                data: data,
                type: 'approved'
              });
            }}
          />
        </Card>
      </AdminLayout>

      <ModalActionVoucherRedeem
        data={modalConfig?.data}
        onClose={handleCloseModal}
        opened={modalConfig?.show}
        type={modalConfig?.type as 'detail' | 'action'}
      />
    </>
  );
};

export default TransaksiRedeemPage;
